import React, { useEffect, useRef, useState } from "react";

import { toast } from "react-toastify";

import useAbortController from "../../../Common/hooks/useAbortController";
import { getSupplyPickupBreadcrumbTitle } from "../../Common/utils";
import InventoryPanel from "./InventoryPanel";
import ItemsRequestedPanel from "./ItemsRequestedPanel";

import { getCustomerAddress } from "../../../../actions/sampleSubmission";
import { getInventoryItems, fetchSupplyRequestDetail } from "../../../../actions/supplyPickup";
import "./CreateEditContent.css";

const mapRecurringRequestToEditingData = (request) => ({
  ...request,
  supply_request_id: request.supply_request_id,
  delivery_details: {
    name: request.contact_name,
    email: request.email,
    phone: request.phone,
    address: request.address,
  },
});

const CreateEditContent = ({
  requestData,
  returnToMainPage,
  onCreateSuccess,
}) => {
  const [requestedItemsMap, setRequestedItemsMap] = useState(new Map());
  const [editingData, setEditingData] = useState(null);
  const [loadingEditingData, setLoadingEditingData] = useState(true);
  const [inventoryItems, setInventoryItems] = useState([]);
  const [allInventoryItems, setAllInventoryItems] = useState([]);
  const [inventoryLoading, setInventoryLoading] = useState(true);
  const [customerInfo, setCustomerInfo] = useState(null);
  const customerIdRef = useRef();
  const { setNewAbortController } = useAbortController();

  const fetchEditingData = async () => {
    setLoadingEditingData(true);
    const { success, result, message } = await fetchSupplyRequestDetail(requestData.data.supply_order_id);
    if (success) {
      setEditingData(result);
      setRequestedItemsMap((prev) => {
        const newMap = new Map(prev);
        result.items.forEach((item) => {
          newMap.set(item.inventory_item_id, { ...item });
        });
        return newMap;
      });
    } else {
      toast.error(message);
    }
    setLoadingEditingData(false);
  };

  const loadRecurringEditingData = (recurringData) => {
    const result = mapRecurringRequestToEditingData(recurringData);
    setEditingData(result);
    setRequestedItemsMap((prev) => {
      const newMap = new Map(prev);
      (result.items || []).forEach((item) => {
        newMap.set(item.inventory_item_id, { ...item });
      });
      return newMap;
    });
    setLoadingEditingData(false);
  };

  const fetchCustomerInfo = async () => {
    const result = await getCustomerAddress(null, "");
    if (result.success) {
      customerIdRef.current = result.address?.customer_id;
    }
    setCustomerInfo(result);
  };

  const fetchInventoryAndLoadRecurringEdit = async (recurringData) => {
    setLoadingEditingData(true);
    setInventoryLoading(true);
    const newAbortController = setNewAbortController();
    const {
      success, result, canceled, message,
    } = await getInventoryItems("", newAbortController.signal, customerIdRef.current);
    if (success) {
      setInventoryItems(result);
      setAllInventoryItems(result);
      loadRecurringEditingData(recurringData);
    } else if (!canceled) {
      setInventoryItems([]);
      setAllInventoryItems([]);
      toast.error(message);
      setLoadingEditingData(false);
    }
    setInventoryLoading(canceled);
  };

  const fetchInventoryItems = async (search = "", fetchEditData = false) => {
    setInventoryLoading(true);
    const newAbortController = setNewAbortController();
    const {
      success, result, canceled, message,
    } = await getInventoryItems(search, newAbortController.signal, customerIdRef.current);
    let supplyItems = [];
    if (success) {
      // Availability checks need the full catalog, not the current search results.
      if (!search) {
        setAllInventoryItems(result);
      }
      supplyItems = result.filter((item) => item.item_name.toLowerCase().includes(search.toLowerCase()));
      setInventoryItems(supplyItems);
    } else if (!canceled) {
      setInventoryItems([]);
      if (!search) {
        setAllInventoryItems([]);
      }
    } else {
      toast.error(message);
    }
    if (fetchEditData) {
      fetchEditingData();
    }
    setInventoryLoading(canceled);
  };

  useEffect(() => {
    const loadInventory = async () => {
      await fetchCustomerInfo();
      if (requestData.data) {
        if (requestData.isRecurring && requestData.data.supply_request_id) {
          fetchInventoryAndLoadRecurringEdit(requestData.data);
        } else {
          fetchInventoryItems("", true);
        }
      } else {
        setLoadingEditingData(false);
        fetchInventoryItems();
      }
    };
    loadInventory();
  }, [requestData]); // eslint-disable-line

  const addItem = (item) => {
    const { inventory_item_id } = item;
    if (!requestedItemsMap.has(inventory_item_id)) {
      setRequestedItemsMap((prev) => {
        const newMap = new Map(prev);
        newMap.set(inventory_item_id, { ...item, quantity: 1 });
        return newMap;
      });
    }
  };

  const updateQuantity = (id, newQuantity) => {
    if (newQuantity <= 0) {
      setRequestedItemsMap((prev) => {
        const newMap = new Map(prev);
        newMap.delete(id);
        return newMap;
      });
    } else {
      setRequestedItemsMap((prev) => {
        const newMap = new Map(prev);
        newMap.get(id).quantity = newQuantity;
        return newMap;
      });
    }
  };

  const pageTitle = getSupplyPickupBreadcrumbTitle(requestData);

  return (
    <div className="CreateEditSupplyPanel">
      <InventoryPanel
        loading={inventoryLoading || loadingEditingData}
        loadingEditingData={loadingEditingData}
        inventoryItems={inventoryItems}
        requestedItemsMap={requestedItemsMap}
        updateQuantity={(id, quantity) => updateQuantity(id, quantity)}
        addItem={addItem}
        fetchInventoryItems={fetchInventoryItems}
        pageTitle={pageTitle}
      />
      <ItemsRequestedPanel
        requestedItemsMap={requestedItemsMap}
        inventoryItems={allInventoryItems}
        inventoryLoading={inventoryLoading}
        updateQuantity={(id, quantity) => updateQuantity(id, quantity)}
        returnToMainPage={returnToMainPage}
        onCreateSuccess={onCreateSuccess}
        editingData={editingData}
        loadingEditingData={loadingEditingData}
        isRecurring={Boolean(requestData?.isRecurring)}
        customerInfo={customerInfo}
      />
    </div>
  );
};

export default CreateEditContent;
